import { AsyncButtonClickAction } from '@/src/app/utils/button.utils';
import { Component, Input, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FormMode, yesNoShort } from 'src/app/utils/form.utils';
import { DictionaryTranslation } from '../../../patient/models/patient.model';
import { ReviewUpdate } from '../models/review-update.model';
import { requiredIfValidator } from '@shared/validation/validators';
import { updateValueAndValidityForAllControls } from '@shared/validation/validation.utils';
import { BaseComponent } from '@shared/components/base-component/base.component';

import { RadioGroupComponent } from '@shared/components/form/components/radio-group/radio-group.component';
import { CheckboxComponent } from '@shared/components/form/components/checkbox/checkbox.component';
import { TextareaComponent } from '@shared/components/form/components/textarea/textarea.component';
import { ActionButtonsComponent } from '@shared/components/form/components/action-buttons/action-buttons.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'stx-grading-form',
  templateUrl: './grading-form.component.html',
  styleUrls: ['./grading-form.component.scss'],
  imports: [ReactiveFormsModule, RadioGroupComponent, CheckboxComponent, TextareaComponent, ActionButtonsComponent, TranslateModule]
})
export class GradingFormComponent extends BaseComponent implements OnInit {
  private formBuilder = inject(UntypedFormBuilder);

  @Input() numberOfPendingCasesForReviewer: number;
  @Input() preGrades: DictionaryTranslation[];
  @Input() postGrades: DictionaryTranslation[];
  @Input() failPassGrades: DictionaryTranslation[];
  @Input() isPalateTreatment: boolean;
  @Input() gradingTitle: string;
  @Input() submitSurgicalReviewWsCall: (x: ReviewUpdate) => Observable<void>;
  readonly formMode = FormMode;
  readonly yesNo = yesNoShort;
  readonly defaultPreGradeForPalates = 3;

  gradingFormGroup: UntypedFormGroup;
  displayValidationError = false;

  ngOnInit() {
    this.gradingFormGroup = this.createInitialForm();
    this.watchChangesOfAlertSmileTrainCheckbox();
    this.watchChangesOfGrades();
    this.watchChangesOfQualityCheckboxes();
  }

  onSurgicalReviewSubmitted: AsyncButtonClickAction = () => {
    Object.keys(this.gradingFormGroup.controls).forEach(controlName => {
      this.gradingFormGroup.get(controlName).markAsTouched();
    });

    if (!this.checkIfFormIsValid()) {
      this.displayValidationError = true;
      return of(null);
    }
    this.displayValidationError = false;
    const reviewUpdate: ReviewUpdate = this.gradingFormGroup.value;

    reviewUpdate.qaPhotoQuality = this.areQualityCheckboxesUnchecked();

    if (this.isPalateTreatment) {
      reviewUpdate.preGrade = this.defaultPreGradeForPalates;
    }
    return this.submitSurgicalReviewWsCall(reviewUpdate).pipe(
      tap(() => {
        this.gradingFormGroup.reset();
        this.gradingFormGroup.get('qaPhotoQuality').setValue(1);
        this.gradingFormGroup.get('surgeryWasUnnecessary').setValue(false);
        this.gradingFormGroup.get('surgicalResultsNotEvident').setValue(false);
        this.gradingFormGroup.get('qualityOfPhotosTooPoor').setValue(false);
      })
    );
  };

  private createInitialForm() {
    return this.formBuilder.group({
      preGrade: [null, [requiredIfValidator(() => this.shouldPreGradeBeRequired())]],
      postGrade: [null, [requiredIfValidator(() => this.shouldPostGradeBeRequired())]],
      qaPhotoQuality: 1,
      surgeryWasUnnecessary: false,
      surgicalResultsNotEvident: false,
      qualityOfPhotosTooPoor: false,
      alertSmileTrain: false,
      note: [null, [requiredIfValidator(() => this.isAlertSmileTrainSelected)]]
    });
  }

  private isSurgeryWasUnnecessary(): boolean {
    return !!this.gradingFormGroup.get('surgeryWasUnnecessary')?.value;
  }

  private isSurgicalResultsNotEvident(): boolean {
    return !!this.gradingFormGroup.get('surgicalResultsNotEvident')?.value;
  }

  private isQualityOfPhotosTooPoor(): boolean {
    return !!this.gradingFormGroup.get('qualityOfPhotosTooPoor')?.value;
  }

  private isAlertSmileTrainChecked(): boolean {
    return !!this.gradingFormGroup.get('alertSmileTrain')?.value;
  }

  private getPreGradeValue(): number | null {
    return this.gradingFormGroup.get('preGrade')?.value;
  }

  private getPostGradeValue(): number | null {
    return this.gradingFormGroup.get('postGrade')?.value;
  }

  private getNoteValue(): string | null {
    return this.gradingFormGroup.get('note')?.value;
  }

  private areQualityCheckboxesUnchecked(): boolean {
    return !this.isSurgicalResultsNotEvident() && !this.isQualityOfPhotosTooPoor();
  }

  private shouldPreGradeBeRequired(): boolean {
    if (!this.gradingFormGroup) {
      return false;
    }

    const surgeryWasUnnecessary = this.isSurgeryWasUnnecessary();

    return (
      (!surgeryWasUnnecessary && this.areQualityCheckboxesUnchecked()) ||
      (surgeryWasUnnecessary && this.areQualityCheckboxesUnchecked() && !this.isPalateTreatment)
    );
  }

  private shouldPostGradeBeRequired(): boolean {
    if (!this.gradingFormGroup) {
      return false;
    }

    const surgeryWasUnnecessary = this.isSurgeryWasUnnecessary();

    return (
      (!surgeryWasUnnecessary && this.areQualityCheckboxesUnchecked()) || (surgeryWasUnnecessary && this.areQualityCheckboxesUnchecked())
    );
  }

  private checkIfFormIsValid(): boolean {
    if (this.isAlertSmileTrainChecked()) {
      if (!this.getNoteValue() || this.getNoteValue().trim() === '') {
        return false;
      }
    }

    if (this.isSurgicalResultsNotEvident() || this.isQualityOfPhotosTooPoor()) {
      return true;
    }
    return this.checkIfScoringIsProvided() || this.checkIfNoteProvidedWhenScoringProvidedAndAlertSmileTrainCheckboxChecked();
  }

  private checkIfScoringIsProvided(): boolean {
    const alertSmileTrain = this.isAlertSmileTrainChecked();
    const preGrade = this.getPreGradeValue();
    const postGrade = this.getPostGradeValue();

    if (!this.isPalateTreatment && !alertSmileTrain) {
      return !!preGrade && !!postGrade;
    }

    if (this.isPalateTreatment && !alertSmileTrain) {
      return !!postGrade;
    }

    return false;
  }

  private checkIfNoteProvidedWhenScoringProvidedAndAlertSmileTrainCheckboxChecked(): boolean {
    const alertSmileTrain = this.isAlertSmileTrainChecked();
    const preGrade = this.getPreGradeValue();
    const postGrade = this.getPostGradeValue();
    const note = this.getNoteValue();

    const validityCondition = !!postGrade && !!note && note !== '';

    if (!this.isPalateTreatment && alertSmileTrain) {
      return validityCondition && !!preGrade;
    }

    if (this.isPalateTreatment && alertSmileTrain) {
      return validityCondition;
    }

    return false;
  }

  get isAlertSmileTrainSelected(): boolean {
    return this.isAlertSmileTrainChecked();
  }

  private watchChangesOfAlertSmileTrainCheckbox(): void {
    this.subSink.sink = this.gradingFormGroup.get('alertSmileTrain').valueChanges.subscribe(value => {
      updateValueAndValidityForAllControls(this.gradingFormGroup);
    });
  }

  private watchChangesOfGrades(): void {
    this.subSink.sink = this.gradingFormGroup.get('preGrade').valueChanges.subscribe(() => {
      updateValueAndValidityForAllControls(this.gradingFormGroup);
    });
    this.subSink.sink = this.gradingFormGroup.get('postGrade').valueChanges.subscribe(() => {
      updateValueAndValidityForAllControls(this.gradingFormGroup);
    });
  }

  private watchChangesOfQualityCheckboxes(): void {
    this.subSink.sink = this.gradingFormGroup.get('surgeryWasUnnecessary').valueChanges.subscribe(() => {
      updateValueAndValidityForAllControls(this.gradingFormGroup);
    });
    this.subSink.sink = this.gradingFormGroup.get('surgicalResultsNotEvident').valueChanges.subscribe(() => {
      updateValueAndValidityForAllControls(this.gradingFormGroup);
    });
    this.subSink.sink = this.gradingFormGroup.get('qualityOfPhotosTooPoor').valueChanges.subscribe(() => {
      updateValueAndValidityForAllControls(this.gradingFormGroup);
    });
  }
}
